import React, { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import Layout from "../components/Layout";
import BurArticlesList from "../components/grants/bur/BurArticlesList";
import ModuleMovedBanner from "../components/ModuleMovedBanner";
import apiClient from "../services/api";
import { useHotel } from "../contexts/HotelContext";
import {
  BUR_ARTICLES_HREF,
  BUR_SNAPSHOT_LABEL,
} from "../constants/legacyGrantsRoutes";

interface ArticleFilters {
  voivodeship?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}

interface SortConfig {
  column:
    | "scrapedAt"
    | "publishedAt"
    | "applicationDateFrom"
    | "applicationDateTo";
  order: "ASC" | "DESC";
}

const BurArticles: React.FC = () => {
  const [filters, setFilters] = useState<ArticleFilters>({
    page: 1,
    limit: 50,
  });
  const [currentSort, setCurrentSort] = useState<SortConfig | null>({
    column: "scrapedAt",
    order: "DESC",
  });
  const { user } = useHotel();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["bur-articles", filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.voivodeship)
        params.append("voivodeship", filters.voivodeship);
      if (filters.page) params.append("page", filters.page.toString());
      if (filters.limit) params.append("limit", filters.limit.toString());
      if (filters.sortBy) params.append("sortBy", filters.sortBy);
      if (filters.sortOrder) params.append("sortOrder", filters.sortOrder);

      const { data } = await apiClient.get(`/grants/bur/articles?${params}`);
      return data;
    },
  });

  const handleVoivodeshipChange = (voivodeship: string) => {
    setFilters((prev) => ({ ...prev, voivodeship, page: 1 }));
  };

  const handlePageChange = (newPage: number) => {
    setFilters((prev) => ({ ...prev, page: newPage }));
  };

  const handleLimitChange = (newLimit: number) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleSortChange = (sortBy: string, sortOrder: string) => {
    if (sortBy === "" && sortOrder === "") {
      // Reset to default sorting
      setFilters((prev) => ({
        ...prev,
        sortBy: undefined,
        sortOrder: undefined,
        page: 1,
      }));
      setCurrentSort({
        column: "scrapedAt",
        order: "DESC",
      });
    } else {
      // Apply new sorting
      setFilters((prev) => ({
        ...prev,
        sortBy,
        sortOrder,
        page: 1,
      }));
      setCurrentSort({
        column: sortBy as
          | "scrapedAt"
          | "publishedAt"
          | "applicationDateFrom"
          | "applicationDateTo",
        order: sortOrder as "ASC" | "DESC",
      });
    }
  };

  const refreshArticles = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Artykuły BUR - Baza Usług Rozwojowych
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Wszystkie artykuły z operatorów Bazy Usług Rozwojowych
          </p>
        </div>

        <ModuleMovedBanner
          description="Ta strona zawiera dane historyczne — nie jest już aktualizowana. Aktualne nabory BUR (PSF) znajdziesz w nowym module Naborów."
          ctaLabel="Przejdź do aktualnych naborów BUR"
          ctaHref={BUR_ARTICLES_HREF}
          dataSnapshotLabel={BUR_SNAPSHOT_LABEL}
        />

        <div data-testid="grants-table-container" className="bg-white rounded-lg shadow-sm border border-neutral-200">
          {isLoading ? (
            <div className="p-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-sm text-neutral-600">
                Ładowanie artykułów BUR...
              </p>
            </div>
          ) : error ? (
            <div className="p-6 text-center text-red-600">
              <p>Błąd podczas pobierania artykułów BUR</p>
            </div>
          ) : (
            <BurArticlesList
              articles={data?.articles || []}
              pagination={data?.pagination}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
              voivodeships={data?.voivodeshipCounts}
              selectedVoivodeship={filters.voivodeship}
              onVoivodeshipChange={handleVoivodeshipChange}
              onSortChange={handleSortChange}
              currentSort={currentSort}
              userRole={user?.role}
              onArticlesRefresh={refreshArticles}
            />
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BurArticles;
